import * as yup from 'yup';
const CIN_REGEX = /^[A-Z]{1}[0-9]{5}[A-Z]{2}[0-9]{4}[A-Z]{3}[0-9]{6}$/;
import dayjs from 'dayjs';

/**
 * Validation schema for new job
 * @param {string} alignment - Alignment of the job posting (tj-jobs, client-jobs)
 * @param {string} jobPreference - Job preference (Full time, Part time, Both)
 * @returns {Object} Yup validation schema
 */
export const NewJobSchema = (alignment, jobPreference) => yup.object().shape({
  title: yup.string().required('Title is required'),
  function: yup.string().required('Function is required'),
  role: yup.string().required('Designation is required'),
  description: yup.string().required('Job description is required.'),
  qualifications: yup.array().of(yup.string()).min(1, "Please select atleast one qualification."),
  company: yup.string().required('Company name is required'),
  min: yup
    .number()
    .typeError('Minimum experience must be a number')
    .required('Min experience is required')
    .min(0, 'Min experience cannot be negative'),
  max: yup
    .number()
    .typeError('Maximum experience must be a number')
    .required('Max experience is required')
    .test('max-greater-than-min', 'Max experience must be greater than or equal to Min experience', function (value) {
      const { min } = this.parent;
      return value >= min;
    }),
  work_mode: yup.string().required('Please select option. Job format is required'),
  company_overview: yup.string().required('Company overview is required.'),
  job_type: yup.string().required('Please select option. Job type is required'),
  job_shift: yup.string().required('Please select option. Job shift is required'),
  industry_sector: yup.string().required('Please specify the industry sector for the job'),
  compensation: jobPreference === 'Full time' ? yup.string().required('Please select a compensation structure') : yup.string().nullable().notRequired(),
  notice_period: jobPreference === 'Full time' ? yup.string().required('Please select option. Notice Period is required') : yup.string().nullable().notRequired(),
  location: yup.string().required('Job location is required'),
  direct_reports: yup.string().required('Please enter the number of people reporting'),
  salary: alignment === 'tj-jobs'
    ? yup
      .number()
      .typeError('Please enter a valid number')
      .min(1, 'Salary must be above zero')
      .required('Please enter your salary')
    : yup
      .string()
      .nullable()
      .notRequired(),
  external_link: alignment === 'tj-jobs'
    ? yup.string().nullable().notRequired()
    : yup.string()
      .nullable()
      .required('Please enter job application link')
      .matches(
        /^$|^((https?:\/\/)?(www\.)?)?[a-zA-Z0-9-]+\.[a-zA-Z]{2,}(\.[a-zA-Z]{2,})?(\/[^\s]*)?$/,
        { excludeEmptyString: true, message: 'Please enter a valid job application link.' }
      ),
  company_identification_number: yup.string().nullable(),
  company_website: yup.string().required('Please enter company website URL')
    // .nullable() // Allow null 
    // .notRequired()
    .matches(
      /^$|^((https?:\/\/)?(www\.)?)?[a-zA-Z0-9-]+\.[a-zA-Z]{2,}(\.[a-zA-Z]{2,})?(\/[^\s]*)?$/,
      { excludeEmptyString: true, message: 'Please enter a valid website URL.' }),
  weekly_time_commitment: (jobPreference !== 'Full time') ? yup
    .number()
    .typeError('Please enter a valid number')
    .min(1, 'Weekly time commitment must be above zero')
    .required('Please enter your weekly time commitment') :
    yup.string().nullable(),
  total_time_commitment_requirement: yup
    .number()
    .transform((value, originalValue) => originalValue === '' ? undefined : value)
    .typeError('Please enter a valid number')
    .when(['compensation_model'], {
      is: (compensation_model) => jobPreference === 'Part time' && compensation_model === 'per hour',
      then: schema => schema.required('Please enter your total time commitment').min(1, 'Total time commitment must be above zero'),
      otherwise: schema => schema.nullable(),
    }),
  compensation_model: (jobPreference !== 'Full time') ? yup.string().required('Please enter compensation model.') : yup.string().nullable(),
  start_date: (jobPreference !== 'Full time') ? yup.date().required('Please select start date.').min(dayjs().startOf('day').toDate(), 'Start date cannot be in the past.') : yup.string().nullable(),
});

/**
 * Schema for validating the details of a new company job posting.
 * 
 * @param {string} alignment - Determines if the job is aligned with 'tj-jobs' or another type,
 *                             impacting required fields.
 * 
 * Fields:
 * - title: The job title, required.
 * - company: The company object, required.
 * - function: The function within the company, required.
 * - role: The job designation, required.
 * - description: A detailed job description, required.
 * - qualifications: An array of qualifications required for the job, at least one required.
 * - min: Minimum years of experience required, must be a non-negative number.
 * - max: Maximum years of experience required, must be greater than or equal to min.
 * - work_mode: The work mode (e.g., remote, onsite), required.
 * - notice_period: The notice period required, required.
 * - location: The job location, required.
 * - direct_reports: Number of direct reports, required.
 * - salary: The salary for the position, must be a number, required.
 * - job_shift: The job shift, required.
 * - external_link: Optional job application link, required if alignment is not 'tj-jobs',
 *                  must be a valid URL format.
 * - company_identification_number: The company identification number, optional.
 * - company_website: The company website URL, optional, must be a valid URL format.
 * - is_company_name_confidential: Boolean indicating if the company name is confidential.
 * - is_compensation_confidential: Boolean indicating if the compensation details are confidential.
 */

export const NewCompanyJobSchema = (alignment) => yup.object().shape({
  title: yup.string().required('Title is required'),
  company: yup.object().required('Company name is required'),
  function: yup.string().required('Function is required'),
  role: yup.string().required('Designation is required'),
  description: yup.string().required('Job description is required.'),
  qualifications: yup.array().of(yup.string()).min(1, "Please select atleast one qualification."),
  min: yup
    .number()
    .required('Min experience is required')
    .min(0, 'Min experience cannot be negative'),
  max: yup
    .number()
    .required('Max experience is required')
    .test('max-greater-than-min', 'Max experience cannot be greater than or equal to Min experience', function (value) {
      const { min } = this.parent;
      return value >= min;
    }),
  work_mode: yup.string().required('Job format is required'),
  notice_period: yup.string().required('Notice Period is required'),
  location: yup.string().required('Job location is required'),
  direct_reports: yup.string().required('Please enter the number of people reporting'),
  salary: yup.string().required('Salary is required').matches(/^[0-9]+$/, 'Salary must be a number'),
  job_shift: yup.string().required('Please select option. Job shift is required'),
  external_link: alignment === 'tj-jobs'
    ? yup.string().nullable().notRequired()
    : yup.string()
      .nullable()
      .required('Please enter job application link')
      .matches(
        /^$|^((https?:\/\/)?(www\.)?)?[a-zA-Z0-9-]+\.[a-zA-Z]{2,}(\.[a-zA-Z]{2,})?(\/[^\s]*)?$/,
        { excludeEmptyString: true, message: 'Please enter a valid job application link.' }
      ),
  company_identification_number: yup.string().nullable(),
  company_website: yup.string().required('Please enter company website URL')
    // .nullable() // Allow null 
    // .notRequired()
    .matches(
      /^$|^((https?:\/\/)?(www\.)?)?[a-zA-Z0-9-]+\.[a-zA-Z]{2,}(\.[a-zA-Z]{2,})?(\/[^\s]*)?$/,
      { excludeEmptyString: true, message: 'Please enter a valid website URL.' }),
  is_company_name_confidential: yup.boolean('Please select option.keep company name confidentail'),
  is_compensation_confidential: yup.boolean('Please select option.keep compensation confidentail'),
});
